import React from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { taskScheduleApis } from "../../../api/TaskSchedule";

// Bulk "Monthly Schedule" upload — rows are {Task ID, Month, Scheduled
// Quantity}: one row per task-month. Thin wrapper over the shared staged-upload
// modal. Project-scoped: the Task ID (task sequence number) resolves within
// this project. The upload REPLACES the full schedule of every task present in
// the file (tasks not in the file are untouched), so re-upload is idempotent.
// A task whose month quantities don't sum to its work quantity gets a WARNING
// on its rows — visible in the preview but never blocking the commit.

const generateTaskScheduleTemplate = () => {
  // Compact summary layout — one row per task, months packed into the last
  // column. Only Task ID and "Schedule Quantity (Month: Qty)" are read by the
  // upload; the middle columns are informational context for the planner.
  const headers = [
    "Task ID",
    "Internal Number",
    "Description",
    "BOQ Unit",
    "BOQ Rate",
    "Total Quantity",
    "Schedule Quantity (Month: Qty)",
    "Reason",
  ];
  const sampleRows = [
    [
      "AE-0100/B3.1.1",
      "B3.1.1",
      "Class II",
      "cum",
      1841.57,
      2019.99,
      "Jul 2026: 2019.99",
      "",
    ],
    [
      "AE-0100/B3.1.1(1)",
      "B3.1.1",
      "Class III",
      "cum",
      1841.57,
      8938.15,
      "Jul 2026: 1895.97; Aug 2026: 4146.43; Sep 2026: 2895.75",
      "",
    ],
    [
      "AE-0100/B1.1",
      "B1.1",
      "Dewatering of Underground Construction sites",
      "KWh",
      24,
      105630.91,
      "Jul 2026: 29670.65; Aug 2026: 31416.86; Sep 2026: 29474.89; Nov 2026: 15068.51",
      "Geology slip at face 2 — dewatering extended past planned end",
    ],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [
    { wch: 20 },
    { wch: 15 },
    { wch: 44 },
    { wch: 9 },
    { wch: 10 },
    { wch: 14 },
    { wch: 70 },
    { wch: 44 },
  ];

  const instructions = [
    ["Monthly Schedule Upload — Instructions"],
    [""],
    ["Task ID", "Required. The task's number (sequence id) shown in the app, within THIS project — including any (1)/(2) suffix. One row per task."],
    ["Schedule Quantity (Month: Qty)", "Required. The month-wise phasing as 'Month: Qty' pairs separated by semicolons, e.g. 'Jul 2026: 1895.97; Aug 2026: 4146.43'. Months also accepted as 'July 2026', '2026-07', or the short 'Jan26' form (2-digit year = 20xx). 0 is allowed; negatives are rejected."],
    ["Internal Number / Description / BOQ Unit / BOQ Rate / Total Quantity", "Informational only — shown for the planner's reference, NOT read by the upload. BOQ Rate is the Basic Rate (net of GST and labour cess), matching the reports."],
    ["Reason", "Optional — required in spirit when months fall OUTSIDE the task's planned start/end dates (a re-forecast): say why, e.g. 'geology slip at face 2'. Stored on the out-of-window months. Missing reason on an out-of-window month shows a warning (does not block)."],
    [""],
    ["Alternate layout", "A long format is also accepted: a sheet named 'Monthly ...' with columns Task ID | Month | Scheduled Quantity — one row per task-month."],
    ["Replace rule", "Uploading REPLACES the full schedule of every task present in the file — a task's previous months are cleared and the file's months become its schedule. Tasks not in the file are untouched."],
    ["Only quantity tasks", "Only quantity-based tasks (with a work quantity) can be scheduled. Rows for Open tasks are rejected."],
    ["Duplicates", "The same Task ID + Month may appear only once in the file — duplicates are rejected."],
    ["Total check", "If a task's month quantities don't sum to its work quantity, its rows get a WARNING in the preview. Differences within ±1 unit are tolerated silently (rounding noise). Warnings never block the save."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 32 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // the backend parser prefers the sheet whose header has "Schedule Quantity
  // (...)", then a "Monthly ..." named sheet, then the first sheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Schedule Summary");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "task-schedule-upload-template.xlsx");
};

const rowErrors = (record: any): string[] =>
  Array.isArray(record?.Error)
    ? record.Error
    : record?.Error
    ? [record.Error]
    : [];

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      if (record?.status === "Applied") return <Tag color="blue">Saved</Tag>;
      if (record?.Warning) return <Tag color="gold">Warning</Tag>;
      return <Tag color="green">Valid</Tag>;
    },
  },
  { title: "Task ID", dataIndex: "TaskID", key: "TaskID", width: 160 },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 200, ellipsis: true },
  { title: "Month", dataIndex: "Month", key: "Month", width: 100 },
  { title: "Scheduled Qty", dataIndex: "ScheduledQty", key: "ScheduledQty", width: 120, align: "right" as const },
  {
    title: "Warning",
    dataIndex: "Warning",
    key: "Warning",
    width: 240,
    render: (value: any) =>
      value ? (
        <span style={{ color: "#b58a1f", fontSize: 12 }}>{value}</span>
      ) : null,
  },
  {
    title: "Reason",
    dataIndex: "Reason",
    key: "Reason",
    width: 200,
    ellipsis: true,
    render: (value: any) =>
      value ? <span style={{ fontSize: 12 }}>{value}</span> : null,
  },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const warningRows = rows.filter((row) => !row?.Error && row?.Warning);
  const validRows = rows.filter((row) => !row?.Error);
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `Valid (${validRows.length})`, rows: validRows },
    { key: "warnings", label: `Warnings (${warningRows.length})`, rows: warningRows },
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const summaryTags = (report: UploadReportState) => {
  const warningCount = report.rows.filter(
    (row: any) => !row?.Error && row?.Warning
  ).length;
  if (!warningCount) return null;
  return <Tag color="gold">{warningCount} warning(s)</Tag>;
};

const TaskScheduleUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Monthly Schedule (Excel)",
      entityLabel: "Monthly Schedule",
      templateSource: { kind: "client", generate: generateTaskScheduleTemplate },
      startUpload: (formData) => taskScheduleApis.uploadExcel(formData),
      buildBody: (comments) => ({
        projectId: String(projectId),
        comment: comments ? comments : "N/A",
      }),
      previewColumns,
      previewScrollX: 1380,
      bucketRows,
      summaryTags,
      // Schedule files run to thousands of rows — full-screen preview,
      // confirmation-gated close, and an offline Excel report for triage.
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "task-schedule-upload-report.xlsx",
        columns: [
          {
            header: "Status",
            width: 10,
            value: (row: any) =>
              row.Error
                ? "Error"
                : row.status === "Applied"
                ? "Saved"
                : row.Warning
                ? "Warning"
                : "Valid",
          },
          { header: "Task ID", width: 20, value: (row: any) => row.TaskID || "" },
          { header: "Task", width: 32, value: (row: any) => row.taskName || "" },
          { header: "Month", width: 10, value: (row: any) => row.Month || "" },
          { header: "Scheduled Qty", width: 14, value: (row: any) => row.ScheduledQty ?? "" },
          { header: "Warning", width: 44, value: (row: any) => row.Warning || "" },
          { header: "Reason", width: 32, value: (row: any) => row.Reason || "" },
          {
            header: "Error Details",
            width: 44,
            value: (row: any) =>
              Array.isArray(row.Error) ? row.Error.join("; ") : row.Error || "",
          },
        ],
      },
      rowErrorLabel: (row: any) => (row.TaskID ? `Task ${row.TaskID}` : "Row"),
      copy: {
        uploadIntro: (
          <p>
            Set the month-wise production schedule for many tasks at once — one
            row per task, with the months packed into{" "}
            <b>Schedule Quantity (Month: Qty)</b> (e.g.{" "}
            <i>Jul 2026: 1895.97; Aug 2026: 4146.43</i>). Uploading{" "}
            <b>replaces</b> each listed task's whole schedule; tasks not in the
            file are untouched. You'll see a per-month preview before anything
            is saved.
          </p>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Resolving each Task ID and month. This normally takes a few seconds to a few minutes depending on the file size.",
        committingHeading: "Saving schedules…",
        committingSub:
          "Replacing each task's schedule with the file's months. Valid rows save; invalid rows are skipped.",
        commitCta: "Save Schedules",
        doneHeading: "Schedules saved",
        doneSub: (report: UploadReportState) => (
          <>
            {report.successCount} row(s) saved
            {report.failureCount ? `, ${report.failureCount} skipped` : ""}.
            Each task's Monthly Production Schedule now reflects the file.
          </>
        ),
        creationFailedNote:
          "Some rows could not be saved — see the errors in the preview.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId],
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default TaskScheduleUploadModal;
